// src/pages/Inspections.js
import React, { useEffect, useState, useRef } from "react";
import "../styles/Templates.css";
import { api } from "../lib/api";

export default function Inspections() {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuCoords, setMenuCoords] = useState({ x: 0, y: 0 });
  const menuRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (openMenuId && menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [openMenuId]);

  useEffect(() => {
    async function fetchInspections() {
      setLoading(true);
      try {
        const data = await api.get("/inspections");
        setInspections(data);
      } finally {
        setLoading(false);
      }
    }
    fetchInspections();
  }, []);

  // --- Load projects to resolve project names from inspection answers ---
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const list = await api.get("/projects");
        setProjects(Array.isArray(list) ? list : []);
      } catch {
        // non-fatal
      }
    })();
  }, []);

  // --- Load templates once to resolve template names for older inspections ---
  const [templatesById, setTemplatesById] = useState({});
  useEffect(() => {
    (async () => {
      try {
        const list = await api.get("/templates");
        const map = {};
        (Array.isArray(list) ? list : []).forEach(t => {
          const title = t.pages?.[0]?.title || t.title || "Untitled";
          map[t.id] = title;
        });
        setTemplatesById(map);
      } catch {
        // non-fatal
      }
    })();
  }, []);

  // Resolve templateId & template name robustly for legacy/variant shapes
  const getTemplateId = (insp) => {
    const cands = [
      insp.templateId,
      insp.template_id,
      insp.templateID,
      insp.templateid,
      insp.template,          // sometimes plain id is stored as "template"
      insp.meta?.templateId,  // we now send this from FillInspection
      insp.answers?.__templateId,
      insp.template?.id,
    ];
    return cands.find(v => v !== undefined && v !== null && String(v).trim?.() !== "");
  };

  const resolveTemplateName = (insp) => {
    const tId = getTemplateId(insp);
    return (
      insp.templateName ||
      insp.meta?.templateName ||          // filled from FillInspection
      insp.answers?.__templateName ||     // legacy/alt
      templatesById[tId] ||               // fallback from /templates lookup
      insp.template?.title ||
      insp.template?.name ||
      insp.templateTitle ||
      "Untitled"
    );
  };

  const filteredInspections = inspections
    .filter((insp) => {
      const q = search.toLowerCase().trim();
      const tName = resolveTemplateName(insp).toLowerCase();
      const byName = tName.includes(q);
      const byDoc  = q && String(insp.documentNumber || "").includes(q);
      return byName || byDoc;
    })
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

  // ===== Row actions =====
  const handleContinue = (insp) => {
    // pass both templateId and inspectionId if available
    const tId = insp.templateId || insp.template_id || insp.template?.id;
    const url = tId
      ? `/inspections/fill/${tId}?inspectionId=${insp.id}`
      : `/inspections/fill/${insp.id}`;
    window.location.href = url;
  };

  const handleView = (insp) => {
    const tId = insp.templateId || insp.template_id || insp.template?.id;
    const url = tId
      ? `/inspections/fill/${tId}?inspectionId=${insp.id}&readonly=1`
      : `/inspections/fill/${insp.id}?readonly=1`;
    window.open(url, "_blank");
  };

  const handleExportPdf = (insp) => {
    const tId = insp.templateId || insp.template_id || insp.template?.id;
    const url = tId
      ? `/inspections/fill/${tId}?inspectionId=${insp.id}&readonly=1&print=1`
      : `/inspections/fill/${insp.id}?readonly=1&print=1`;
    window.open(url, "_blank");
  };

  const handleSendEmail = (insp) => {
    const tId = insp.templateId || insp.template_id || insp.template?.id;
    const link = window.location.origin + (
      tId
        ? `/inspections/fill/${tId}?inspectionId=${insp.id}&readonly=1`
        : `/inspections/fill/${insp.id}?readonly=1`
    );
    const subject = encodeURIComponent(`Inspection: ${insp.templateName || insp.id}`);
    const body = encodeURIComponent(`View/print the inspection here:\n${link}\n\n(Use the Print dialog to save as PDF.)`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  // Safe DELETE that works with api.delete, api.del, or falls back to fetch
  const doDelete = async (url) => {
    if (typeof api.delete === "function") return api.delete(url);
    if (typeof api.del === "function")    return api.del(url);
    const base = process.env.REACT_APP_API_URL || "";
    const res = await fetch(`${base}${url}`, { method: "DELETE", headers: { "Content-Type": "application/json" } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    try { return await res.json(); } catch { return {}; }
  };

  const handleDelete = async (insp) => {
    if (!window.confirm("Delete this inspection?")) return;
    try {
      const id = encodeURIComponent(insp.id);
      await doDelete(`/inspections/${id}`);
      setInspections((prev) => prev.filter((i) => i.id !== insp.id));
    } catch (e) {
      // Try to surface the real failure reason
      let msg = e?.message || "";
      if (e?.response?.data?.error) msg = e.response.data.error;
      if (!msg && e?.responseText) msg = e.responseText;
      console.error("Delete error detail:", e);
      alert(`Delete failed${msg ? `: ${msg}` : "."}`);
    }
  };

  function statusBadge(status) {
    if (status === "complete")
      return (
        <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">
          Complete
        </span>
      );
    if (status === "in_progress")
      return (
        <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">
          In Progress
        </span>
      );
    return (
      <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-700">
        {status}
      </span>
    );
  }

  return (
    <div className="p-6 w-full">
      <div className="bg-white rounded-2xl shadow-md p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
          <h1 className="text-2xl font-bold text-gray-800">
            Inspections
          </h1>
        </div>
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <input
            className="border rounded px-3 py-2 w-full md:w-1/2"
            placeholder="Search by template name or document #…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>
      <div className="bg-white rounded-2xl shadow-md">
        {loading ? (
          <div className="p-6 text-gray-500">Loading inspections…</div>
        ) : filteredInspections.length === 0 ? (
          <div className="p-6 text-gray-400">No inspections found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto">
              <thead>
                <tr className="bg-blue-50 text-blue-800">
                  <th className="py-3 px-5 text-left font-bold">Template</th>
                  <th className="py-3 px-5 text-left font-bold">Project</th>
                  <th className="py-3 px-5 text-left font-bold">Date and Time</th>
                  <th className="py-3 px-5 text-left font-bold">Doc #</th>
                  <th className="py-3 px-5 text-left font-bold">Status</th>
                  <th className="py-3 px-5 text-right font-bold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInspections.map((insp) => {
                  // Resolve project name:
                  // 1) use explicit fields if present
                  // 2) otherwise scan answers and see if any value equals a known project id
                  let projectName =
                    insp.project?.name ||
                    insp.projectName ||
                    insp.meta?.project ||
                    "—";

                  if (!projectName || projectName === "—") {
                    const ans = insp.answers || {};
                    const vals = Object.values(ans);
                    const matchId = vals.find(v =>
                      projects.some(p => String(p.id) === String(v))
                    );
                    if (matchId != null) {
                      const proj = projects.find(p => String(p.id) === String(matchId));
                      if (proj) projectName = proj.name || proj.title || `Project ${proj.id}`;
                    }
                  }

                  const resolvedTemplateName = resolveTemplateName(insp);
                  const isComplete = (insp.status || "").toLowerCase() === "complete";
                  return (
                    <tr key={insp.id} className="border-b hover:bg-blue-50 transition">
                      <td className="py-3 px-5 font-medium text-gray-800">
                        {resolvedTemplateName}
                      </td>
                      <td className="py-3 px-5 text-gray-700">
                        {projectName}
                      </td>
                      <td className="py-3 px-5 text-gray-600">
                        {insp.createdAt
                          ? new Date(insp.createdAt).toLocaleString()
                          : ""}
                      </td>
                      <td className="py-3 px-5 text-gray-800">
                        {typeof insp.documentNumber === "number" ? `#${insp.documentNumber}` : "—"}
                      </td>
                      <td className="py-3 px-5">
                        {statusBadge(insp.status)}
                      </td>
                      <td className="py-3 px-5">
                        <div className="flex items-center justify-end gap-2 relative">
                          {isComplete ? (
                            <button
                              onClick={() => handleView(insp)}
                              className="px-3 py-1 rounded-md text-sm bg-blue-600 text-white hover:bg-blue-700"
                            >
                              View Inspection
                            </button>
                          ) : (
                            <button
                              onClick={() => handleContinue(insp)}
                              className="px-3 py-1 rounded-md text-sm bg-blue-600 text-white hover:bg-blue-700"
                            >
                              Continue Inspection
                            </button>
                          )}

                          <button
                            onClick={(e) => {
                              if (openMenuId === insp.id) {
                                setOpenMenuId(null);
                                return;
                              }
                              const rect = e.currentTarget.getBoundingClientRect();
                              const MENU_W = 192; // 12rem
                              const MENU_H = 140; // approx
                              const openUp = window.innerHeight - rect.bottom < MENU_H + 24;
                              const x = rect.right - MENU_W;
                              const y = openUp ? rect.top - MENU_H - 8 : rect.bottom + 8;
                              setMenuCoords({ x, y });
                              setOpenMenuId(insp.id);
                            }}
                            className="w-8 h-8 grid place-items-center rounded-md hover:bg-gray-100"
                            title="More"
                          >
                            ⋯
                          </button>

                          {openMenuId === insp.id && (
                            <div
                              ref={menuRef}
                              style={{ position: "fixed", left: menuCoords.x, top: menuCoords.y, zIndex: 1000 }}
                              className="w-48 bg-white border rounded-md shadow-lg"
                            >
                              <button
                                onClick={() => { setOpenMenuId(null); handleSendEmail(insp); }}
                                className="block w-full text-left px-3 py-2 hover:bg-gray-50"
                              >
                                Send inspection (email)
                              </button>
                              <button
                                onClick={() => { setOpenMenuId(null); handleExportPdf(insp); }}
                                className="block w-full text-left px-3 py-2 hover:bg-gray-50"
                              >
                                Export as PDF
                              </button>
                              <button
                                onClick={() => { setOpenMenuId(null); handleDelete(insp); }}
                                className="block w-full text-left px-3 py-2 text-red-600 hover:bg-red-50"
                              >
                                Delete inspection
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
